import { useSortableTable } from '../../../hooks/useSortableTable';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../../utils/api';
import PageHeader from '../../../components/PageHeader';

function StockLevels() {
  const navigate = useNavigate();
  const [stockItems, setStockItems] = useState([]);
  
  // Sorting
  const { items: sortedItems, requestSort, getSortIndicator } = useSortableTable(stockItems, { key: 'product_name', direction: 'asc' });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const columnMenuRef = useRef(null);
  const [filterView, setFilterView] = useState('all'); // all, low-stock
  const [expandedCategories, setExpandedCategories] = useState({});

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    productCode: true,
    productName: true,
    type: true,
    category: true,
    totalQuantity: true,
    totalReserved: true,
    totalAvailable: true,
    warehouseCount: true,
    warehouses: false,
    location: false,
  });

  // Available columns configuration
  const columns = [
    { key: 'productCode', label: 'Product Code' },
    { key: 'productName', label: 'Product Name' },
    { key: 'type', label: 'Type' },
    { key: 'category', label: 'Category' },
    { key: 'totalQuantity', label: 'Total Stock' },
    { key: 'totalReserved', label: 'Reserved' },
    { key: 'totalAvailable', label: 'Available' },
    { key: 'warehouseCount', label: 'Warehouses' },
    { key: 'warehouses', label: 'Warehouse Names' },
    { key: 'location', label: 'Primary Location' },
  ];

  useEffect(() => {
    fetchStockLevels();
    loadPreferences();
  }, [filterView]);

  const loadPreferences = async () => {
    try {
      const response = await apiFetch('/api/preferences/default/stock-levels');
      const data = await response.json();
      
      if (data.preferences) {
        setVisibleColumns(data.preferences);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const savePreferences = async (newPreferences) => {
    try {
      await apiFetch('/api/preferences/default/stock-levels', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: newPreferences })
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  useEffect(() => {
    savePreferences(visibleColumns);
  }, [visibleColumns]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchStockLevels = async () => {
    setLoading(true);
    try {
      const endpoint = filterView === 'low-stock' 
        ? '/api/stock-levels/low-stock'
        : '/api/stock-levels';
        
      const response = await apiFetch(endpoint);
      const data = await response.json();
      setStockItems(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stock levels:', error);
      setLoading(false);
    }
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const filteredItems = sortedItems.filter(item =>
    item.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.product_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.product_category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group by category
  const groupedItems = filteredItems.reduce((acc, item) => {
    const category = item.product_category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  const categories = Object.keys(groupedItems).sort();

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleAllCategories = (expand) => {
    const newState = {};
    categories.forEach(cat => {
      newState[cat] = expand;
    });
    setExpandedCategories(newState);
  };

  const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader 
        title="Stock Levels"
        subtitle={`${stockItems.length} products in stock`}
        backButton
        onBack={() => navigate('/inventory-landing')}
        backTitle="Back to Inventory"
        sticky
      />

      <main className="max-w-full mx-auto px-6 py-8">
        {/* Search and Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <div className="relative" ref={columnMenuRef}>
                <button
                  onClick={() => setShowColumnMenu(!showColumnMenu)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center gap-2"
                >
                  <span>⚙️</span>
                  <span>Columns</span>
                </button>
                
                {showColumnMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-700">Show/Hide Columns</h3>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {columns.map(column => (
                        <label
                          key={column.key}
                          className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={visibleColumns[column.key]}
                            onChange={() => toggleColumn(column.key)}
                            className="mr-3 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{column.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilterView('all')}
              className={`px-4 py-2 rounded-lg transition ${
                filterView === 'all'
                  ? 'bg-gray-400 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Stock
            </button>
            <button
              onClick={() => setFilterView('low-stock')}
              className={`px-4 py-2 rounded-lg transition ${
                filterView === 'low-stock'
                  ? 'bg-gray-400 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Low Stock
            </button>
          </div>
        </div>

        {/* Expand/Collapse All */}
        {!loading && filteredItems.length > 0 && (
          <div className="mb-4 flex gap-2">
            <button
              onClick={() => toggleAllCategories(true)}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
            >
              ▼ Expand All
            </button>
            <button
              onClick={() => toggleAllCategories(false)}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
            >
              ▶ Collapse All
            </button>
            <span className="text-sm text-gray-500 ml-2 self-center">
              {categories.length} categories • {filteredItems.length} products
            </span>
          </div>
        )}

        {/* Grouped Table */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-gray-400">Loading stock levels...</div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No stock found</h3>
            <p className="text-gray-500">
              {searchTerm ? 'Try adjusting your search' : 'No products currently in stock'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {categories.map(category => (
              <div key={category} className="bg-white rounded-lg shadow-sm overflow-hidden">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 flex items-center justify-between text-left transition"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-gray-600 font-mono text-sm">
                      {expandedCategories[category] ? '▼' : '▶'}
                    </span>
                    <h3 className="font-semibold text-gray-800">{category}</h3>
                    <span className="text-sm text-gray-500">
                      ({groupedItems[category].length} products)
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Total Stock: {groupedItems[category].reduce((sum, item) => sum + parseFloat(item.total_quantity || 0), 0).toFixed(0)}
                  </div>
                </button>

                {/* Category Items */}
                {expandedCategories[category] && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {visibleColumns.productCode && (
                      <th 
                        onClick={() => requestSort('product_code')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-1">
                          Product Code
                          {getSortIndicator('product_code')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.productName && (
                      <th 
                        onClick={() => requestSort('product_name')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-1">
                          Product Name
                          {getSortIndicator('product_name')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.type && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                    )}
                    {visibleColumns.category && (
                      <th 
                        onClick={() => requestSort('product_category')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-1">
                          Category
                          {getSortIndicator('product_category')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.totalQuantity && (
                      <th 
                        onClick={() => requestSort('total_quantity')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-1">
                          Total Stock
                          {getSortIndicator('total_quantity')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.totalReserved && (
                      <th 
                        onClick={() => requestSort('total_reserved')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-1">
                          Reserved
                          {getSortIndicator('total_reserved')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.totalAvailable && (
                      <th 
                        onClick={() => requestSort('total_available')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-1">
                          Available
                          {getSortIndicator('total_available')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.warehouseCount && (
                      <th 
                        onClick={() => requestSort('warehouse_count')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-1">
                          Warehouses
                          {getSortIndicator('warehouse_count')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.warehouses && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Warehouse Names
                      </th>
                    )}
                    {visibleColumns.location && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Primary Location
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {groupedItems[category].map((item, index) => (
                    <tr 
                      key={item.product_id}
                      className={index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'}
                    >
                      {visibleColumns.productCode && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-mono text-sm font-semibold text-blue-600">
                            {item.product_code}
                          </span>
                        </td>
                      )}
                      {visibleColumns.productName && (
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{item.product_name}</div>
                        </td>
                      )}
                      {visibleColumns.type && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {item.type}
                          </span>
                        </td>
                      )}
                      {visibleColumns.category && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{item.product_category || '—'}</div>
                        </td>
                      )}
                      {visibleColumns.totalQuantity && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">{parseFloat(item.total_quantity || 0).toFixed(1)}</span>
                        </td>
                      )}
                      {visibleColumns.totalReserved && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-orange-600">{parseFloat(item.total_reserved || 0).toFixed(1)}</span>
                        </td>
                      )}
                      {visibleColumns.totalAvailable && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm font-semibold ${
                            parseFloat(item.total_available || 0) < 10 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {parseFloat(item.total_available || 0).toFixed(1)}
                          </span>
                        </td>
                      )}
                      {visibleColumns.warehouseCount && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{item.warehouse_count || 0}</span>
                        </td>
                      )}
                      {visibleColumns.warehouses && (
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {item.warehouses && item.warehouses.length > 0 
                              ? item.warehouses.join(', ') 
                              : '—'}
                          </div>
                        </td>
                      )}
                      {visibleColumns.location && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{item.location || '—'}</div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default StockLevels;
